import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';
import { Moment } from 'moment';

interface DiscountImpactAnalysisProps {
    dateRange?: [Moment, Moment];
}

const DiscountImpactAnalysis: React.FC<DiscountImpactAnalysisProps> = ({ dateRange }) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        hasDiscount: boolean;
        productCount: number;
        totalViews: number;
        avgViewsPerProduct: number;
        uniqueViews: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const startDate = dateRange?.[0]?.toISOString();
                const endDate = dateRange?.[1]?.toISOString();
                const result = await analyticsService.getDiscountImpact(startDate, endDate);
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch discount impact');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch discount impact');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const labels = data.map(item => item.hasDiscount ? 'On Sale' : 'Regular Price');
    const avgViews = data.map(item => item.avgViewsPerProduct);
    const colors = [theme.palette.success.main, theme.palette.info.main];

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Discount Impact Analysis
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Average views per product: Discounted vs Regular
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 300, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <>
                        <BarChart
                            xAxis={[{
                                data: labels,
                                scaleType: 'band',
                                tickLabelStyle: {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.primary,
                                    fontWeight: 500,
                                    fontFamily: 'primaryFont'
                                }
                            }]}
                            series={[{
                                data: avgViews,
                                label: 'Avg Views',
                            }]}
                            colors={colors}
                            height={300}
                            margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                            slotProps={{
                                legend: { hidden: true }
                            }}
                            sx={{
                                '& .MuiChartsAxis-tickLabel': {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.secondary,
                                    fontFamily: 'primaryFont',
                                },
                                '& .MuiChartsAxis-line': {
                                    stroke: theme.palette.divider,
                                },
                                '& .MuiBarElement-root': {
                                    rx: 4,
                                },
                            }}
                        />
                        <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {data.map((item, index) => (
                                <Box key={index} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, justifyContent: 'center' }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: colors[index],
                                            }}
                                        />
                                        <Typography variant='body2' fontFamily='primaryFont' fontWeight={600}>
                                            {item.hasDiscount ? 'On Sale' : 'Regular Price'}
                                        </Typography>
                                    </Box>
                                    <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold'>
                                        {item.avgViewsPerProduct.toFixed(1)}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                        {item.productCount} products • {item.totalViews} total views
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default DiscountImpactAnalysis;

